import React from 'react';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { getTabStripSortableId } from '../../../utils/workspaceTabPersistence';

export function getSortableTabId(tab) {
  return getTabStripSortableId({ type: tab.type, id: tab.id });
}

export function resolveTabFromEntry(entry, tabMaps) {
  if (!entry) return null;
  const tabs = tabMaps[entry.type];
  const tab = tabs?.find((t) => String(t.id) === String(entry.id));
  if (!tab) return null;
  if (entry.type === 'request') {
    return {
      ...tab,
      requestProtocol: tab.requestProtocol || tab.type || 'http',
      type: 'request',
    };
  }
  return { ...tab, type: entry.type };
}

export const DNDKIT_MODIFIERS = [restrictToHorizontalAxis];

/** px — the Terminal strip (TerminalExtView) uses the same number. */
export const TAB_STRIP_HEIGHT = 36;

export function getTabMethod(tab) {
  if (tab.type !== 'request') return null;
  const proto = tab.requestProtocol || 'http';
  if (proto === 'sse' || proto === 'websocket' || proto === 'grpc') return proto.toUpperCase();
  return tab.method || 'GET';
}

export function isBlankRows(rows) {
  return !rows || rows.every((r) => !r?.key && !r?.value);
}

/**
 * A pristine "New Request" tab: GET, empty URL/body/name, no edits, no saved
 * origin, no response. Closing the last request tab already recreates one of
 * these, so closing it when it's already pristine is a no-op — we hide its
 * close button in that case. Any other request tab can always be closed.
 */
export function isDefaultNewRequestTab(tab) {
  if (!tab || tab.type !== 'request') return false;
  if (tab.originalId || tab.collectionId) return false;
  if ((tab.requestProtocol || 'http') !== 'http') return false;
  if ((tab.method || 'GET') !== 'GET') return false;
  if (tab.url || tab.body || tab.name) return false;
  if (tab.preRequestScript || tab.testScript) return false;
  if (tab.response) return false;
  return isBlankRows(tab.headers) && isBlankRows(tab.params) && isBlankRows(tab.cookies);
}

export function isRequestTabCloseable(tab, requestTabCount) {
  if (tab.pinned) return false;
  if (tab.type !== 'request') {
    return tab.type === 'collection' || tab.type === 'schema' || tab.type === 'db-manager-table' || tab.type === 'task-manager-board' || tab.type === 'projects-project' || tab.type === 'document-editor';
  }
  return requestTabCount > 1 || !isDefaultNewRequestTab(tab);
}

/**
 * The hover text for a tab.
 *
 * For most tabs this is just the title, and the `title` attribute is then a
 * courtesy for names too long to fit. For a DB tab it has to do real work: the
 * label is the bare table name, so `users` from staging and `users` from
 * production render as two identical tabs, and the only way to tell them apart
 * was to click one and see what came back — on a table you are about to edit,
 * the wrong moment to find out.
 */
export function getTabTooltip(tab, t) {
  const title = getTabTitle(tab, t);
  if (tab.type !== 'db-manager-table') return title;

  const lines = [];
  if (tab.view === 'overview') lines.push(`Overview: ${tab.label}`);
  else if (tab.tableName) lines.push(`Table: ${tab.tableName}`);
  // A query tab has no table; naming the script is what distinguishes it.
  else if (tab.label) lines.push(`Query: ${tab.label}`);
  if (tab.dbName) lines.push(`Database: ${tab.dbName}`);
  // connLabel is written at tab creation and is display-only — connId is the
  // authoritative identity, and resolving a human name from it here would mean
  // reaching into the vault from the tab strip.
  if (tab.connLabel) lines.push(`Connection: ${tab.connLabel}`);

  // Nothing beyond the title to add: fall back rather than showing a lone
  // "Table: users" line that says less than the tab already does.
  return lines.length > 1 ? lines.join('\n') : title;
}

/**
 * `getTabTooltip` answers with NEWLINES for a DB tab ("Table / Database /
 * Connection"). The native `title` rendered those as lines; the app's Tooltip is
 * `white-space: nowrap`, where a newline is a space — three facts on one line,
 * which is exactly the "which `users` is this?" problem that tooltip exists to
 * solve. One element per line keeps them apart.
 */
export function tooltipContent(tooltip) {
  if (typeof tooltip !== 'string' || !tooltip.includes('\n')) return tooltip;
  return (
    <span className="flex flex-col text-left">
      {tooltip.split('\n').map((line) => (
        <span key={line}>{line}</span>
      ))}
    </span>
  );
}

export function getTabTitle(tab, t) {
  if (tab.type === 'collection') return tab.name || t('tabs.collection');
  if (tab.type === 'schema') return tab.name || 'Schema';
  if (tab.type === 'db-manager-table') return tab.label || tab.tableName || 'Table';
  if (tab.type === 'task-manager-board') return tab.label || 'Board';
  if (tab.type === 'projects-project') return tab.label || 'Project';
  if (tab.type === 'document-editor') return tab.label || 'Untitled';
  if (tab.name) return tab.name;
  if (tab.url) {
    try {
      const url = new URL(tab.url);
      return url.hostname || t('request.newRequest');
    } catch {
      return tab.url.length > 20 ? tab.url.substring(0, 20) + '...' : tab.url;
    }
  }
  return t('request.newRequest');
}
